import React, { useEffect, useState } from 'react';

import { translate } from '@vestahealthcare/common/i18n';
import { MemberView, PaginationType } from '@vestahealthcare/common/models';

import { PanelInfo } from 'styleguide-v2';

import { BulkEditTable } from 'dash/src/pages/Ops/Members/BulkEditTable';
import { getMembersView } from 'dash/src/services/PatientServices';

const PAGE_SIZE = 10;

const uniq = (array: any[]) => [...new Set(array)];

interface Props {
  selection: number[];
  setSelection: (selection: number[]) => void;
  memberIds: number[];
  setMemberIds: (memberIds: number[]) => void;
  refresh: number;
  setOuterLoading: (loading: boolean) => void;
}

export const PODShuffleScheduleTab = ({
  selection,
  setSelection,
  memberIds,
  setMemberIds,
  refresh,
  setOuterLoading,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [members, setMembers] = useState<MemberView[]>([]);
  const [pagination, setPagination] = useState<PaginationType>();

  const getMembersList = async (ids: number[]) => {
    const offset = page * pageSize;
    if (offset && offset + pageSize < members?.length + 1) return;
    setLoading(true);
    setOuterLoading(true);

    const idsToFetch = [...ids].slice(offset, offset + pageSize);
    const { items } = await getMembersView({
      id: idsToFetch,
      offset: 0,
      limit: pageSize,
    });

    if (offset !== 0) {
      setMembers([...members, ...items]);
    } else {
      setMembers(items);
    }
    setPagination({
      limit: pageSize,
      offset,
      total: ids.length,
    } as PaginationType);
    setLoading(false);
    setOuterLoading(false);
  };

  useEffect(() => {
    if (selection.length === 0 && refresh > 1) {
      setMemberIds([]);
      setMembers([]);
      setPagination(undefined);
      setPage(0);
    }
  }, [selection, refresh]);

  useEffect(() => {
    if (memberIds.length) {
      getMembersList(memberIds);
    } else {
      setMembers([]);
      setPagination(undefined);
      setLoading(false);
      setOuterLoading(false);
    }
  }, [page, pageSize, memberIds, refresh]);

  const updateSelection = (newSelection: number[]) => {
    setSelection(newSelection);
  };

  if (!memberIds.length && !loading) {
    return (
      <PanelInfo
        type="info"
        title={translate('pods.shuffle.schedule.noMembers')}
      />
    );
  }

  return (
    <div>
      <BulkEditTable
        allChecked={
          !!(
            members?.length &&
            members?.every((member) => selection.includes(member.id))
          )
        }
        members={members || []}
        membersSelected={selection}
        onChangePage={setPage}
        onChangePageSize={(ps) => {
          setMembers([]);
          setPage(0);
          setPageSize(ps);
        }}
        onClickCheckbox={(member) =>
          updateSelection(
            selection.includes(member.id)
              ? selection.filter((id) => member.id !== id)
              : uniq([...selection, member.id]),
          )
        }
        onClickCheckboxAll={(checked) => {
          const ids = members?.map((member) => member.id) || [];
          if (!ids.length) return;

          if (checked) {
            updateSelection(selection.filter((id) => !ids.includes(id)));
          } else {
            updateSelection(uniq([...selection, ...ids]));
          }
        }}
        pagination={pagination}
      />
    </div>
  );
};
